import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { CircularProgress } from '@mui/material';
import { red } from '@mui/material/colors';
import { AuthContext } from '../AuthContext';
import { Paper } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PasswordIcon from '@mui/icons-material/Password';
import InputAdornment from '@mui/material/InputAdornment';

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="https://entrinsic.io/">
        Entrinsic Connect
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const defaultTheme = createTheme();

// Create a new LoginForm component that uses the reCAPTCHA hook
function LoginForm({ setShowNotVerifiedModal, setShowAlert, navigate, setShowBrowserVerificationModal, setShowBrowserCodeModal }) {
    const [loggingIn, setLoggingIn] = useState(false);
    const [lastLoginAttempt, setLastLoginAttempt] = useState(null);
    const [retryAfterVerification, setRetryAfterVerification] = useState(false);
    const { setUser, setGlobalTenantId } = useContext(AuthContext);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const captchaToken = useRef(null);
    const theme = useTheme();

    const handleReCaptchaVerify = async () => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return null;
        }
        return await executeRecaptcha('login');
    };

    // Effect to retry login after verification
    useEffect(() => {
        if (retryAfterVerification && lastLoginAttempt) {
            const retryWithNewCaptcha = async () => {
                // Regenerate captcha for retry if needed
                let newLoginPayload = { ...lastLoginAttempt };
                
                if (!lastLoginAttempt.email.endsWith('@entrinsic.io')) {
                    console.log('Regenerating captcha for retry login');
                    const newCaptchaToken = await handleReCaptchaVerify();
                    if (newCaptchaToken) {
                        newLoginPayload.captchaToken = newCaptchaToken;
                    }
                }
                
                await performLogin(newLoginPayload, true);
            };
            
            retryWithNewCaptcha();
            setRetryAfterVerification(false);
            setLastLoginAttempt(null);
        }
    }, [retryAfterVerification]);

    const performLogin = async (loginPayload, isRetry = false) => {
        if (!isRetry) setLoggingIn(true);

        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(loginPayload)
            });
            
            // Check if the rate limit has been exceeded
            if (response.status === 429) {
                const errorData = await response.json();
                alert(errorData.message);
                setLoggingIn(false);
                return;
            }

            const data = await response.json();
            
            if (isRetry) {
                console.log('Retry login response:', data);
            }

            if (data.success) {
                const user = {
                    id: data.id,
                    role: data.role,
                    email: data.email,
                    partnerId: data.partnerId,
                    partnerName: data.partnerName,
                    is_partner_user: data.is_partner_user
                };

                setUser(user);
                setGlobalTenantId(null);
                const params = new URLSearchParams(window.location.search);
                let redirectUrl;

                if (data.role =='KIOSK') {
                    redirectUrl = params.get('redirect') || '/kiosk';
                    window.location.href = redirectUrl;
                    return;
                }
                
                if (data.role=='SUPER_ADMIN') {
                    redirectUrl = params.get('redirect') || '/admin/customers';
                } else if (data.role=='PARTNER') {
                    redirectUrl = params.get('redirect') || '/partner-admin';
                } else {
                    redirectUrl = params.get('redirect') || '/client-admin/';
                }
                navigate(decodeURIComponent(redirectUrl));  
        
            } else {
                setLoggingIn(false);
                if (data.reason === 'browser_verification_required') {
                    if (!isRetry) {
                        setLastLoginAttempt(loginPayload);
                        setShowBrowserCodeModal(true);
                    } else {
                        console.error('Still requiring browser verification after successful code verification');
                        alert('Verification failed. Please try logging in manually.');
                    }
                } else if (data.reason === 'not_verified') {
                    setShowNotVerifiedModal(true);  
                    sessionStorage.setItem('verificationToken', data.token);
                } else {
                    if (isRetry) {
                        console.error('Retry login failed:', data);
                        alert(`Login failed after verification: ${data.message || 'Unknown error'}`);
                    } else {
                        alert(data.message);
                    }
                }
            }
        } catch (error) {
            console.error("Login error:", error);
            if (isRetry) {
                alert('Login retry failed due to network error');
            }
            setLoggingIn(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        const data = new FormData(event.currentTarget);
        let email = data.get('email');
        let password = data.get('password');

        // Only get captcha if not a developer email
        if (!email.endsWith('@entrinsic.io')) {
            captchaToken.current = await handleReCaptchaVerify();
        }

        const loginPayload = { 
            email, 
            password,
            ...(captchaToken.current && { captchaToken: captchaToken.current })
        };

        await performLogin(loginPayload);
    };

    // Expose retry function for child components
    window.retryLoginAfterVerification = () => {
        setRetryAfterVerification(true);
    };

    return (
        <Box 
            component="form" 
            onSubmit={handleSubmit} 
            noValidate 
            sx={{ 
                mt: 3,
                width: '100%'
            }}
        >
            <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <EmailIcon color="primary" />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.1)'
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.2)'
                        }
                    }
                }}
            />
            <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <PasswordIcon color="primary" />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.1)'
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.2)'
                        }
                    }
                }}
            />
            
            <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ 
                    mt: 3, 
                    mb: 2,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(58, 123, 213, 0.2)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        boxShadow: '0 6px 16px rgba(58, 123, 213, 0.3)',
                        transform: 'translateY(-2px)'
                    }
                }}
                disabled={loggingIn}
            >
                { loggingIn ? <CircularProgress size={24} /> : 'Sign In' }
            </Button>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <Link 
                        href="/reset-password" 
                        variant="body2"
                        sx={{
                            textDecoration: 'none',
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            transition: 'color 0.2s ease',
                            '&:hover': {
                                color: theme.palette.primary.dark,
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        Forgot password?
                    </Link>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
                    <Link 
                        href="/signup" 
                        variant="body2"
                        sx={{
                            textDecoration: 'none',
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            transition: 'color 0.2s ease',
                            '&:hover': {
                                color: theme.palette.primary.dark,
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {"Don't have an account? Sign Up"}
                    </Link>
                </Grid>
            </Grid>
        </Box>
    );
}

// Browser Code Verification Modal Component
function BrowserCodeModal({ open, onClose, onVerified }) {
    const [code, setCode] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState('');
    const theme = useTheme();

    const handleSubmit = async (event, codeValue = null) => {
        if (event) event.preventDefault();
        
        const submitCode = codeValue || code;
        if (submitCode.length !== 5) {
            setError('Please enter a 5-digit code');
            return;
        }

        setVerifying(true);
        setError('');

        try {
            const response = await fetch('/api/verify-browser-code', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ code: submitCode })
            });

            const data = await response.json();

            if (data.success) {
                onClose();
                setCode('');
                onVerified();
                
                // Add a small delay to ensure cookies are set, then trigger retry login
                setTimeout(() => {
                    if (window.retryLoginAfterVerification) {
                        window.retryLoginAfterVerification();
                    }
                }, 100);
            } else {
                setError(data.message || 'Invalid verification code');
            }
        } catch (error) {
            console.error('Code verification error:', error);
            setError('Failed to verify code. Please try again.');
        } finally {
            setVerifying(false);
        }
    };

    const processCodeInput = (value) => {
        // Extract only digits and limit to 5 characters
        const cleanCode = value.replace(/\D/g, '').slice(0, 5);
        setCode(cleanCode);
        if (error) setError('');
        
        // Auto-submit when we have exactly 5 digits
        if (cleanCode.length === 5) {
            setTimeout(() => {
                handleSubmit(null, cleanCode); // Pass the clean code directly
            }, 100); // Small delay to allow state to update
        }
    };

    const handleChange = (e) => {
        processCodeInput(e.target.value);
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        processCodeInput(pastedText);
    };

    const handleClose = () => {
        setCode('');
        setError('');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    minWidth: '400px'
                }
            }}
        >
            <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                Browser Verification Required
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <DialogContentText sx={{ textAlign: 'center', mb: 3 }}>
                    We've sent a 5-digit verification code to your email. 
                    Please enter it below to verify this browser.
                </DialogContentText>
                <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Verification Code"
                        value={code}
                        onChange={handleChange}
                        onPaste={handlePaste}
                        placeholder="12345"
                        inputProps={{
                            maxLength: 5,
                            style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                        }}
                        error={!!error}
                        helperText={error || 'Code will verify automatically when entered'}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&.Mui-focused': {
                                    boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.2)'
                                }
                            }
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
                <Button 
                    onClick={handleClose}
                    variant="outlined"
                    sx={{ 
                        borderRadius: 2,
                        px: 3,
                        minWidth: '100px'
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={verifying || code.length !== 5}
                    sx={{ 
                        borderRadius: 2,
                        px: 3,
                        minWidth: '100px'
                    }}
                >
                    {verifying ? <CircularProgress size={20} /> : 'Verify'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// Main SignIn component
export default function SignIn() {
    const [showNotVerifiedModal, setShowNotVerifiedModal] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [showPasswordResetAlert, setShowPasswordResetAlert] = useState(false);
    const [showPasswordResetSuccessAlert, setShowPasswordResetSuccessAlert] = useState(false);
    const [showBrowserVerificationModal, setShowBrowserVerificationModal] = useState(false);
    const [showBrowserCodeModal, setShowBrowserCodeModal] = useState(false);
    const [showBrowserVerificationSuccessAlert, setShowBrowserVerificationSuccessAlert] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const { setUser } = useContext(AuthContext);

    const handleBrowserVerified = () => {
        setShowBrowserVerificationSuccessAlert(true);
        setTimeout(() => setShowBrowserVerificationSuccessAlert(false), 5000);
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('passwordReset') === 'true') {
            setShowPasswordResetAlert(true);
            setTimeout(() => setShowPasswordResetAlert(false), 10000);  
        }
    }, [location.search]);

    useEffect(() => {
      const params = new URLSearchParams(location.search);
      if (params.get('passwordResetSuccess') === 'true') {
          setShowPasswordResetSuccessAlert(true);
          setTimeout(() => setShowPasswordResetSuccessAlert(false), 10000);  
      }
  }, [location.search]);
  
  const handleResend = async () => {
    const token = sessionStorage.getItem('verificationToken');
    
    try {
        const response = await fetch(`/api/notverified?resend=true&token=${token}`);
        if (response.ok) {
            // Maybe show a toast or some feedback saying the email was resent
            setShowAlert(true); // Show the alert
            setTimeout(() => setShowAlert(false), 10000); 
            setShowNotVerifiedModal(false);
        } else {
            console.error('Failed to resend verification email');
        }
    } catch (error) {
        console.error("Error:", error);
    }
};

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('verified') === 'true') {
            setShowBrowserVerificationSuccessAlert(true);
            setTimeout(() => setShowBrowserVerificationSuccessAlert(false), 10000);
        }
    }, [location.search]);

  return (
    <GoogleReCaptchaProvider reCaptchaKey="6Lc1uyopAAAAANOL6i9Mi0q_sBDncbXU9Uoe1oLv">
      <ThemeProvider theme={defaultTheme}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
            py: 4
          }}
        >
          <Container component="main" maxWidth="sm">
            <CssBaseline />
            <Paper
              elevation={3}
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                background: 'white',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #3a7bd5 0%, #00b8d4 100%)',
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <img 
                    src='/Entrinsic_Logo.png' 
                    alt="Entrinsic Logo" 
                    style={{ 
                      height: '60px',
                      marginBottom: '16px'
                    }}
                  />
                  <Typography 
                    component="h1" 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      mb: 1
                    }}
                  >
                    Sign in
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      maxWidth: '400px',
                      mx: 'auto'
                    }}
                  >
                    Welcome back! Please enter your credentials to access your account.
                  </Typography>
                </Box>

                <LoginForm 
                  setShowNotVerifiedModal={setShowNotVerifiedModal}
                  setShowAlert={setShowAlert}
                  navigate={navigate}
                  setShowBrowserVerificationModal={setShowBrowserVerificationModal}
                  setShowBrowserCodeModal={setShowBrowserCodeModal}
                />
              </Box>
            </Paper>
            <Copyright sx={{ mt: 4, mb: 2 }} />
          </Container>

          <Dialog
            open={showNotVerifiedModal}
            onClose={() => setShowNotVerifiedModal(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <DialogTitle id="alert-dialog-title">Email Not Verified</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                You cannot use the system because you have not yet verified your email.
                Please check your inbox for the verification email.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button 
                onClick={handleResend} 
                color="primary"
                variant="contained"
                sx={{ 
                  borderRadius: 2,
                  px: 3
                }}
              >
                Resend Verification Email
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar 
            open={showAlert} 
            autoHideDuration={6000} 
            onClose={() => setShowAlert(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setShowAlert(false)} 
              severity="success" 
              variant="filled"
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
            >
              Verification email was resent!
            </Alert>
          </Snackbar>

          <Snackbar 
            open={showPasswordResetAlert} 
            autoHideDuration={15000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              severity="success"
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
            >
              Your password reset link was sent if the email address used exists - check your inbox.
            </Alert>
          </Snackbar>

          <Snackbar 
            open={showPasswordResetSuccessAlert} 
            autoHideDuration={15000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              severity="success"
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
            >
              Your password has been successfully reset. Please log in with your new password.
            </Alert>
          </Snackbar>

          <BrowserCodeModal
            open={showBrowserCodeModal}
            onClose={() => setShowBrowserCodeModal(false)}
            onVerified={handleBrowserVerified}
          />

          <Snackbar 
            open={showBrowserVerificationSuccessAlert} 
            autoHideDuration={10000} 
            onClose={() => setShowBrowserVerificationSuccessAlert(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              severity="success" 
              variant="filled"
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
            >
              Browser verified successfully! You can now log in.
            </Alert>
          </Snackbar>
        </Box>
      </ThemeProvider>
    </GoogleReCaptchaProvider>
  );
}

